import { type UseQueryResult, useQuery } from '@tanstack/react-query';

import { type AppError, labReviewKeys, request } from '@/shared/api';

import type { LabReview } from '../model/types';
import { labReviewListSchema, toLabReviews } from './schema';

/**
 * The signed-in patient's reviews, newest first — the open one with its
 * payment button on top of «مشاوره», the rest as «سوابق». Whose they are
 * comes from the session; there is no id to pass.
 *
 * Short-lived: a review is paid on the bank page in another tab, and a card
 * still saying «منتظر پرداخت» after the patient paid is the worst answer.
 */
export function useMyLabReviews(
  options: {
    readonly enabled?: boolean;
    /** The «پاسخ تازه» watch on every patient screen; «مشاوره» itself does not poll. */
    readonly refetchInterval?: number;
  } = {},
): UseQueryResult<readonly LabReview[], AppError> {
  return useQuery<readonly LabReview[], AppError>({
    queryKey: labReviewKeys.mine(),
    queryFn: async ({ signal }): Promise<readonly LabReview[]> =>
      toLabReviews(
        await request('/lab-reviews', { schema: labReviewListSchema, signal, auth: 'account' }),
      ),
    enabled: options.enabled ?? true,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
    refetchInterval: options.refetchInterval ?? false,
  });
}

/**
 * One patient's reviews, as the clinic sees them — for the grant button to
 * know whether one is already open, and for the patient's file.
 * `lab_reviews.grant` or `visits.read_clinical`; a 403 is an answer.
 */
export function usePatientLabReviews(
  patientId: string | null,
  options: { readonly enabled?: boolean } = {},
): UseQueryResult<readonly LabReview[], AppError> {
  return useQuery<readonly LabReview[], AppError>({
    queryKey: labReviewKeys.ofPatient(patientId ?? ''),
    queryFn: async ({ signal }): Promise<readonly LabReview[]> =>
      toLabReviews(
        await request(`/staff/patients/${patientId ?? ''}/lab-reviews`, {
          schema: labReviewListSchema,
          signal,
        }),
      ),
    enabled: patientId !== null && (options.enabled ?? true),
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * «سوابق» on the panel's «بررسی آزمایش»: every review with a conversation,
 * newest first. The queue above it holds only what waits on the doctor; an
 * answered review leaves it and is found here. `lab_reviews.review`.
 */
export function useLabReviewHistory(
  options: { readonly enabled?: boolean } = {},
): UseQueryResult<readonly LabReview[], AppError> {
  return useQuery<readonly LabReview[], AppError>({
    queryKey: labReviewKeys.history(),
    queryFn: async ({ signal }): Promise<readonly LabReview[]> =>
      toLabReviews(await request('/staff/lab-reviews', { schema: labReviewListSchema, signal })),
    enabled: options.enabled ?? true,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}
